/**
 * Market Evidence Queue — per-line recovery engine.
 *
 * Extracted from the report route so the calculation can be certified by
 * automated tests. Rendering, layout and chip styling live in the route and
 * are unchanged; this module owns only the numbers and the documented reason
 * attached to every row.
 *
 * Invariants enforced here (see market-evidence.test.ts):
 *  1. Every line's recovery is computed independently from that line's own
 *     invoice amount and its own benchmark band.
 *  2. A line's recovery can never exceed the amount billed on that line.
 *  3. A finding is attached to a line only when it is unambiguously
 *     line-specific; aggregate findings that span several lines are never
 *     attributed to one of them.
 *  4. A finding is consumed by at most one line, so repeated charge
 *     categories can never share or duplicate the same dollars.
 *  5. Every row carries either a positive recovery or a documented
 *     no-recovery reason — the UI can never render a blank status.
 */

import type { FindingItem, MarketComparison as MarketComparisonRow } from "@/lib/db/models";

export type MarketAssessment =
  | "Within Market"
  | "Above Market"
  | "Materially Above Market"
  | "Below Market"
  | "Unclear";

export type RecoveryBasis = "benchmark" | "non-market" | "none";

/**
 * Internal (non-rendered) audit trail for every recovery amount. Retained on
 * the row so support and QA can explain any figure without re-deriving it.
 */
export interface RecoveryProvenance {
  /** `finding_items.id` when an explicit line-specific finding drove the value. */
  sourceFindingId: string | null;
  /** Human-readable source of the number. */
  sourceType:
    | "line-specific-finding"
    | "benchmark-band-ceiling"
    | "benchmark-median-overcharge"
    | "documented-difference"
    | "none";
  calculationBasis: RecoveryBasis;
  /** `market_comparisons.id` for the invoice line this figure belongs to. */
  invoiceLineId: string | null;
  /** Why this line does or does not support a recovery. */
  rationale: string;
  /** ISO timestamp of the calculation. */
  computedAt: string;
  /** Benchmark confidence 0-100 when the provider supplied one. */
  confidence: number | null;
  /** True when the raw basis amount was reduced to the line's billed amount. */
  capped: boolean;
  /** Raw amount before capping, for reconciliation. */
  rawAmount: number;
  /** True when a positive basis rounded to $0 for display (sub-dollar overage). */
  roundedToZero: boolean;
}

export interface MarketRow {
  service: string;
  invoiceCharge: number | null;
  localAverage: number | null;
  low: number | null;
  high: number | null;
  variancePct: number | null;
  assessment: MarketAssessment;
  savingsLow: number;
  savingsHigh: number;
  /**
   * Where `savingsHigh` came from, so the UI can state a documented reason for
   * every row instead of leaving the recovery slot blank:
   *  - "benchmark"  → the charge exceeds the verified local benchmark band.
   *  - "non-market" → a line-specific forensic finding, or a documented
   *                   difference on a line that carries no verified band
   *                   (duplicate billing, regulatory cap, documentation).
   *  - "none"       → no recovery is supported for this line.
   */
  savingsBasis: RecoveryBasis;
  /** True when this line has a usable verified market band / median. */
  hasBenchmark: boolean;
  confidence: number | null;
  source: string | null;
  provenance: RecoveryProvenance;
}

export function classifyVariance(pct: number | null): MarketAssessment {
  if (pct === null) return "Unclear";
  if (pct >= 25) return "Materially Above Market";
  if (pct >= 8) return "Above Market";
  if (pct <= -8) return "Below Market";
  return "Within Market";
}

/**
 * Normalized comparison key: lowercase, punctuation → space, collapsed, with
 * trailing plurals folded so "Administrative Fees" === "Administrative Fee".
 * Parenthetical qualifiers ("(Daily Rate)", "14 miles") are preserved as
 * tokens because they distinguish repeated units of the same charge type.
 */
export function normalizeMatchKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((t) => (t.length > 3 && t.endsWith("s") && !t.endsWith("ss") ? t.slice(0, -1) : t))
    .join(" ");
}

/**
 * Documented aliases only. Substring matching is deliberately NOT used: it let
 * "Administrative Fee" capture "Duplicate Administrative Fee" (and one finding
 * leak across several operator/helper lines).
 */
const CHARGE_ALIASES: Record<string, string> = {
  "admin fee": "administrative fee",
  "administration fee": "administrative fee",
  "office fee": "administrative fee",
  "gate fee": "gate access fee",
  "after hour release fee": "after hours release",
  "after hours release fee": "after hours release",
  "lien fee": "lien processing",
  "lien processing fee": "lien processing",
  "storage fee": "outdoor storage daily rate",
  "cc fee": "credit card convenience fee",
  "credit card fee": "credit card convenience fee",
  "fuel surcharge": "fuel recovery charge",
  "environmental fee": "environmental recovery fee",
  "winch fee": "winch recovery",
  "winching": "winch recovery",
};

function canonicalKey(s: string): string {
  const k = normalizeMatchKey(s);
  return CHARGE_ALIASES[k] ?? k;
}

/** True when `needle` appears in `haystack` as a contiguous whole-token run. */
function containsTokenPhrase(haystack: string, needle: string): boolean {
  if (!haystack || !needle) return false;
  const h = haystack.split(" ");
  const n = needle.split(" ");
  if (n.length > h.length) return false;
  for (let i = 0; i + n.length <= h.length; i++) {
    let ok = true;
    for (let j = 0; j < n.length; j++) {
      if (h[i + j] !== n[j]) {
        ok = false;
        break;
      }
    }
    if (ok) return true;
  }
  return false;
}

type Candidate = { finding: FindingItem; score: number };

/**
 * Findings that can be attributed to exactly one line, ranked by match
 * strength. Ambiguous titles (matching two or more distinct lines) are
 * aggregate findings and are dropped — they belong to the chapter total, not
 * to any single row.
 */
function buildFindingAssignments(
  lineKeys: string[],
  findings: FindingItem[] | undefined,
): Array<FindingItem | null> {
  const assigned: Array<FindingItem | null> = lineKeys.map(() => null);
  if (!findings?.length) return assigned;

  const candidatesPerLine: Candidate[][] = lineKeys.map(() => []);

  for (const f of findings) {
    const title = canonicalKey(f.title ?? "");
    if (!title) continue;
    const hits: Array<{ index: number; score: number }> = [];
    lineKeys.forEach((key, index) => {
      if (!key) return;
      if (title === key) hits.push({ index, score: 3 });
      // A phrase match only counts for multi-token line names; single words
      // ("storage", "tow") are far too broad to identify a specific line.
      else if (key.split(" ").length >= 2 && containsTokenPhrase(title, key))
        hits.push({ index, score: 2 });
    });
    if (hits.length === 0) continue;
    const exact = hits.filter((h) => h.score === 3);
    // Exactly one exact match wins outright. Otherwise the finding must
    // identify a single line unambiguously, or it is treated as aggregate.
    if (exact.length === 1) {
      candidatesPerLine[exact[0].index].push({ finding: f, score: 3 });
      continue;
    }
    if (exact.length > 1) continue; // duplicate line names → aggregate/ambiguous
    if (hits.length === 1) candidatesPerLine[hits[0].index].push({ finding: f, score: 2 });
  }

  // One finding may only fund one line. Highest score first, ties by amount.
  const taken = new Set<string>();
  const order = lineKeys
    .map((_, i) => i)
    .sort(
      (a, b) =>
        Math.max(0, ...candidatesPerLine[b].map((c) => c.score)) -
        Math.max(0, ...candidatesPerLine[a].map((c) => c.score)),
    );
  for (const i of order) {
    const best = candidatesPerLine[i]
      .filter((c) => !taken.has(c.finding.id))
      .sort((a, b) => b.score - a.score || (b.finding.amount ?? 0) - (a.finding.amount ?? 0))[0];
    if (best) {
      taken.add(best.finding.id);
      assigned[i] = best.finding;
    }
  }
  return assigned;
}

function computeRow(m: MarketComparisonRow, match: FindingItem | null, now: string): MarketRow {
  const service = m.line_item ?? m.service_type ?? "Service";
  const invoiceCharge = m.invoice_amount ?? m.invoice_rate ?? null;
  const localAverage = m.local_average ?? m.market_median ?? null;
  const low = m.typical_low ?? m.market_low ?? null;
  const high = m.typical_high ?? m.market_high ?? null;

  let variancePct: number | null = null;
  if (typeof m.difference_percent === "number") variancePct = m.difference_percent;
  else if (localAverage && invoiceCharge)
    variancePct = Math.round(((invoiceCharge - localAverage) / localAverage) * 100);
  const assessment = classifyVariance(variancePct);

  const overcharge =
    invoiceCharge && localAverage && invoiceCharge > localAverage
      ? invoiceCharge - localAverage
      : m.difference_amount && m.difference_amount > 0
        ? m.difference_amount
        : 0;

  // A line-level recovery can never exceed that line's own billed amount, so a
  // finding larger than the invoice charge is an aggregate figure and is not
  // attributed to this row.
  const rawExplicit = typeof match?.amount === "number" ? match.amount : null;
  const explicit =
    rawExplicit !== null &&
    rawExplicit > 0 &&
    (invoiceCharge === null || rawExplicit <= invoiceCharge)
      ? rawExplicit
      : null;

  // A verified band must actually exist before a "within market" verdict can
  // suppress a recovery. Lines with an all-zero / empty band are unbenchmarked
  // and their documented difference still stands.
  const hasBenchmark =
    (typeof localAverage === "number" && localAverage > 0) ||
    (typeof high === "number" && high > 0) ||
    (typeof low === "number" && low > 0);

  // A line only counts as market-supported when the billed amount actually
  // sits at or below the top of the verified band. A verdict computed against
  // the median alone can read "Within Market" while the charge still clears
  // the band ceiling — that excess is a real, documentable recovery.
  const aboveBandTop =
    typeof high === "number" && high > 0 && invoiceCharge !== null && invoiceCharge > high
      ? invoiceCharge - high
      : 0;
  const withinOrBelow = assessment === "Within Market" || assessment === "Below Market";
  const marketSupported = hasBenchmark && withinOrBelow && aboveBandTop <= 0;

  // Benchmark-derived overcharge only counts on lines the benchmark flags. On
  // a market-supported line the median delta is not a recovery; on a "within
  // market" line that still clears the ceiling, the recovery is measured
  // conservatively against that ceiling.
  const benchmarkAmount = hasBenchmark && withinOrBelow ? aboveBandTop : overcharge;
  const basisAmount = explicit ?? (marketSupported ? 0 : benchmarkAmount);
  const capAmount = invoiceCharge !== null ? Math.max(0, invoiceCharge) : basisAmount;
  const capped = invoiceCharge !== null ? Math.min(basisAmount, capAmount) : basisAmount;
  const savingsHigh = Math.max(0, Math.round(capped));
  const savingsLow = Math.max(0, Math.round(capped * 0.7));

  const savingsBasis: RecoveryBasis =
    savingsHigh <= 0 ? "none" : explicit !== null || !hasBenchmark ? "non-market" : "benchmark";
  const confidence =
    typeof m.confidence === "number" ? Math.round((m.confidence as number) * 100) : null;

  // Provenance describes the *basis*, not the rounded display value: a
  // sub-dollar overage rounds to $0 on screen but still has a documented
  // origin, which the audit trail must preserve.
  const sourceType: RecoveryProvenance["sourceType"] =
    basisAmount <= 0
      ? "none"
      : explicit !== null
        ? "line-specific-finding"
        : !hasBenchmark
          ? "documented-difference"
          : withinOrBelow
            ? "benchmark-band-ceiling"
            : "benchmark-median-overcharge";

  const rationale =
    basisAmount > 0
      ? sourceType === "line-specific-finding"
        ? `Line-specific forensic finding "${match?.title ?? ""}" quantified at ${explicit}.`
        : sourceType === "documented-difference"
          ? "No verified market band for this line; documented difference retained as non-market recovery."
          : sourceType === "benchmark-band-ceiling"
            ? `Billed amount clears the verified band ceiling (${high}); recovery measured against the ceiling.`
            : `Billed amount exceeds the verified local median (${localAverage}).`
      : !hasBenchmark
        ? "No usable market benchmark exists for this line and no line-specific finding was attributed."
        : marketSupported
          ? `Billed amount sits within the verified range (${low}–${high}).`
          : "No recovery supported: benchmark comparison produced no positive difference.";

  return {
    service,
    invoiceCharge,
    localAverage,
    low,
    high,
    variancePct,
    assessment,
    savingsLow,
    savingsHigh,
    savingsBasis,
    hasBenchmark,
    confidence,
    source: m.source ?? null,
    provenance: {
      sourceFindingId: explicit !== null ? (match?.id ?? null) : null,
      sourceType,
      calculationBasis: savingsBasis,
      invoiceLineId: m.id ?? null,
      rationale,
      computedAt: now,
      confidence,
      capped: invoiceCharge !== null && basisAmount > capAmount,
      roundedToZero: basisAmount > 0 && savingsHigh === 0,
      rawAmount: basisAmount,
    },
  };
}

/**
 * Build every Market Evidence Queue row for a review. Always use this instead
 * of mapping rows individually: finding attribution is a whole-invoice
 * decision (a finding matching two lines is aggregate, and a finding consumed
 * by one line cannot fund another).
 */
export function buildMarketRows(
  comparisons: MarketComparisonRow[],
  findings?: FindingItem[],
): MarketRow[] {
  const now = new Date().toISOString();
  const keys = comparisons.map((m) => canonicalKey(m.line_item ?? m.service_type ?? ""));
  const assignments = buildFindingAssignments(keys, findings);
  return comparisons.map((m, i) => computeRow(m, assignments[i], now));
}

/** Documented status text for a row. Never empty. */
export function marketRowStatus(row: MarketRow): string {
  if (row.savingsHigh > 0) return row.savingsBasis === "benchmark" ? "recover" : "non-market";
  if (!row.hasBenchmark) return "No Benchmark Available";
  if (row.assessment === "Below Market") return "Below Market";
  if (row.assessment === "Within Market") return "Within Market";
  return "No Recovery Calculated";
}
